import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { AlertTriangle, Bell, CheckCircle2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WATCHED_TYPES = new Set([
  'qc_review_reminder',
  'order_assigned',
  'order_on_hold',
  'lens_reserved_for_order',
  'lens_supplier_order_required',
]);

const ROLE_TYPES = {
  lab_manager: new Set(['qc_review_reminder', 'order_on_hold']),
  admin: new Set(['order_on_hold']),
  technician: new Set(['order_assigned']),
  inventory_manager: new Set(['order_on_hold', 'lens_reserved_for_order', 'lens_supplier_order_required']),
  operations_manager: new Set(['order_on_hold', 'lens_reserved_for_order', 'lens_supplier_order_required']),
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const criticalFlash = keyframes`
  0%, 100% { background: rgba(220, 38, 38, 0.20); }
  50% { background: rgba(220, 38, 38, 0.58); }
`;

const criticalPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 24px 70px -22px rgba(127, 29, 29, 0.75); }
  50% { transform: scale(1.015); box-shadow: 0 34px 90px -18px rgba(127, 29, 29, 0.95); }
`;

const sirenGlow = keyframes`
  0%, 100% { border-color: #ef4444; }
  50% { border-color: #2563eb; }
`;

const Stack = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: min(390px, calc(100vw - 2rem));
  z-index: 100000;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  pointer-events: none;
`;

const CriticalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(220, 38, 38, 0.38);
  animation: ${criticalFlash} 650ms infinite;
  pointer-events: auto;
`;

const CriticalPanel = styled.div`
  width: min(620px, 100%);
  background: #7f1d1d;
  color: white;
  border: 4px solid #ef4444;
  border-radius: 24px;
  padding: 1.4rem;
  text-align: center;
  animation: ${criticalPulse} 850ms infinite, ${sirenGlow} 500ms infinite;

  h2 {
    margin: 0 0 0.5rem;
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  p {
    margin: 0.35rem auto 1rem;
    max-width: 520px;
    color: #fee2e2;
    font-weight: 650;
    line-height: 1.45;
  }
`;

const CriticalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Card = styled.div`
  pointer-events: auto;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.9rem 0.95rem;
  padding-right: 2.4rem;
  border-radius: 14px;
  position: relative;
  animation: ${slideIn} 180ms ease-out both;
  background: ${(p) => (p.$critical ? '#fef2f2' : '#eef2ff')};
  border: 1px solid ${(p) => (p.$critical ? '#fca5a5' : '#c7d2fe')};
  box-shadow:
    0 18px 35px -14px rgba(15, 23, 42, 0.32),
    0 8px 14px -10px rgba(15, 23, 42, 0.18);
`;

const IconWrap = styled.div`
  color: ${(p) => (p.$critical ? '#dc2626' : '#4f46e5')};
  margin-top: 0.1rem;
  flex-shrink: 0;
`;

const Body = styled.div`
  min-width: 0;
  flex: 1;
`;

const Title = styled.div`
  font-weight: 800;
  color: ${(p) => (p.$critical ? '#991b1b' : '#1e1b4b')};
  margin-bottom: 0.18rem;
`;

const Message = styled.div`
  color: #334155;
  font-size: 0.87rem;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.65rem;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 9px;
  padding: 0.45rem 0.7rem;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$primary ? (p.$critical ? '#dc2626' : '#4f46e5') : 'white')};
  color: ${(p) => (p.$primary ? 'white' : '#475569')};
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #64748b;
  border-radius: 7px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover { background: rgba(15, 23, 42, 0.08); color: #0f172a; }
`;

function getUserRole() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role || user.userRole || user.type || localStorage.getItem('user_role') || '';
  } catch (_) {
    return localStorage.getItem('user_role') || '';
  }
}

function shouldShow(role, type) {
  if (!WATCHED_TYPES.has(type)) return false;
  const allowed = ROLE_TYPES[role];
  return allowed ? allowed.has(type) : false;
}

function playNotificationSound(type = 'info') {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const beep = (delay, frequency, duration, volume = 0.18) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = type === 'reminder' ? 'square' : 'sine';
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + delay + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + duration);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(ctx.currentTime + delay);
      oscillator.stop(ctx.currentTime + delay + duration + 0.02);
    };

    if (type === 'critical') {
      for (let i = 0; i < 6; i += 1) {
        beep(i * 0.18, i % 2 === 0 ? 920 : 520, 0.13, 0.2);
      }
      setTimeout(() => ctx.close().catch(() => {}), 1500);
      return;
    }

    if (type === 'reminder') {
      for (let i = 0; i < 14; i += 1) {
        beep(i * 0.14, i % 2 === 0 ? 980 : 430, 0.115, 0.26);
      }
      setTimeout(() => ctx.close().catch(() => {}), 2400);
      return;
    }

    beep(0, 660, 0.13, 0.09);
    beep(0.18, 760, 0.12, 0.09);
    setTimeout(() => ctx.close().catch(() => {}), 450);
  } catch (_) {
    // Browsers may block audio until user interaction; the popup still shows.
  }
}

function soundTypeFor(item) {
  if (item.critical) return 'critical';
  if (item.type === 'qc_review_reminder') return 'reminder';
  return 'assignment';
}

const LiveNotificationListener = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const seenRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    const fetchNotifications = async () => {
      const token = localStorage.getItem('token');
      const role = getUserRole();
      if (!token || !role) return;

      const resp = await axios.get('/api/notifications?limit=10&unread=true', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const incoming = (resp.data?.items || []).filter((n) => shouldShow(role, n.type));

      const fresh = incoming.filter((n) => !seenRef.current.has(String(n._id || n.id)));
      if (fresh.length === 0 || cancelled) return;

      fresh.forEach((n) => seenRef.current.add(String(n._id || n.id)));
      const mapped = fresh.map((n) => ({
        id: String(n._id || n.id),
        title: n.title,
        message: n.body,
        type: n.type,
        link: n.link,
        critical: n.type === 'order_on_hold',
      }));
      setItems((prev) => [...mapped, ...prev].slice(0, 3));
      mapped.forEach((n, idx) => {
        setTimeout(() => playNotificationSound(soundTypeFor(n)), idx * 220);
      });
    };

    fetchNotifications().catch(() => {});
    const interval = setInterval(() => {
      fetchNotifications().catch(() => {});
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const dismiss = async (item, markRead = false) => {
    setItems((prev) => prev.filter((n) => n.id !== item.id));
    if (markRead) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await axios.patch(`/api/notifications/${item.id}/read`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      } catch (_) {}
    }
  };

  const openNotification = async (item) => {
    await dismiss(item, true);
    if (item.link) window.location.assign(item.link);
  };

  if (items.length === 0) return null;

  const criticalItem = items.find((item) => item.critical);

  return (
    <>
      {criticalItem && (
        <CriticalOverlay role="alertdialog" aria-modal="true">
          <CriticalPanel>
            <AlertTriangle size={54} />
            <h2>{t('notifListener.criticalHold')}</h2>
            <p>
              <strong>{criticalItem.title}</strong><br />
              {criticalItem.message}
            </p>
            <CriticalActions>
              {criticalItem.link && (
                <ActionButton $primary $critical onClick={() => openNotification(criticalItem)}>
                  {t('notifListener.checkOnHoldOrder')}
                </ActionButton>
              )}
              <ActionButton onClick={() => dismiss(criticalItem, true)}>
                {t('notifListener.acknowledge')}
              </ActionButton>
            </CriticalActions>
          </CriticalPanel>
        </CriticalOverlay>
      )}

      <Stack aria-live="assertive">
        {items.map((item) => {
          const Icon = item.critical ? AlertTriangle : item.type === 'order_assigned' ? CheckCircle2 : Bell;
          return (
            <Card key={item.id} $critical={item.critical}>
              <IconWrap $critical={item.critical}><Icon size={20} /></IconWrap>
              <Body>
                <Title $critical={item.critical}>{item.title}</Title>
                <Message>{item.message}</Message>
                <Actions>
                  {item.link && (
                    <ActionButton $primary $critical={item.critical} onClick={() => openNotification(item)}>
                      {t('notifListener.open')}
                    </ActionButton>
                  )}
                  <ActionButton onClick={() => dismiss(item, true)}>{t('notifListener.dismiss')}</ActionButton>
                </Actions>
              </Body>
              <CloseButton onClick={() => dismiss(item, item.critical)} aria-label={t('notifListener.closeNotification')}>
                <X size={14} />
              </CloseButton>
            </Card>
          );
        })}
      </Stack>
    </>
  );
};

export default LiveNotificationListener;
